<i18n locale="en" lang="json">
{
  "true": "Yes",
  "false": "No",
  "empty": "--"
}
</i18n>
<i18n locale="nl" lang="json">
{
  "true": "Ja",
  "false": "Nee",
  "empty": "--"
}
</i18n>

<template>
  <div>
    <base-linear-loader v-if="loading" indeterminate />
    <v-row v-set-test-id="[componentName, testId]" dense no-gutters>
      <v-col
        v-for="(col, index) in cols"
        :key="col.id ? `${col.type}-${col.id}` : index"
        v-bind="col.props"
      >
        <slot
          v-if="col.type === ColType.label"
          :name="`label.${col.id || index}`"
          v-bind="col"
        >
          {{ col.label }}
        </slot>
        <template v-if="col.type === ColType.value">
          <slot name="value" v-bind="col">
            <slot :name="`value.${col.id || index}`" v-bind="col">
              <base-router-link
                v-if="col.to"
                v-set-test-id:a="[componentName, testId, col.to]"
                :to="col.to"
              >
                <template v-if="typeof col.value === 'boolean'">
                  {{ t(col.value ? 'true' : 'false') }}
                </template>
                <template v-else>{{ col.value || '&nbsp;' }}</template>
              </base-router-link>
              <a
                v-else-if="col.href"
                v-set-test-id="[componentName, testId, col.href]"
                :href="col.href"
                :target="isExternalLink(col.href) ? '_blank' : undefined"
              >
                <template v-if="typeof col.value === 'boolean'">{{
                  t(col.value ? 'true' : 'false')
                }}</template>
                <template v-else>{{ col.value || '&nbsp;' }}</template>
                <font-awesome-icon
                  v-if="col.href && isExternalLink(col.href)"
                  :icon="faExternalLink"
                  class="ml-1"
                />
              </a>
              <markdown v-else-if="col.markdown" :content="col.markdown" />
              <template v-else>
                <template v-if="typeof col.value === 'boolean'">
                  {{ t(col.value ? 'true' : 'false') }}
                </template>
                <template v-else-if="typeof col.value === 'number'">
                  {{ n(col.value as number) }}
                </template>
                <template
                  v-else-if="
                    typeof col.value === 'string' &&
                    col.value.match(
                      /^(\/Date\(-?\d+\)\/)|(\d{4}-\d{2}-\d{2}([\sT]\d{2}:\d{2}:\d{2})?)([.]\d{1,3})?$/,
                    )
                  "
                >
                  {{ d(parseToDate(col.value), 'short') }}
                </template>
                <template v-else-if="col.value">
                  {{ col.value }}
                </template>
                <template v-else>{{
                  loading ? '&nbsp;' : t('empty')
                }}</template>
              </template>
            </slot>
          </slot>
          <span
            v-if="col.message"
            :class="
              isVertical ? 'd-inline text-disabled' : 'd-block font-italic'
            "
          >
            {{ isVertical ? '&nbsp;' : '' }}
            <slot :name="`message.${col.id || index}`" v-bind="col">
              {{ col.message }}
            </slot>
          </span>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import { isEmpty } from 'underscore';
import type { ZvdZLocationRaw } from '@/plugins/router';
import isExternalLink from '@/common/is-external-link';
import type { ActionableComponentProps } from '@/common/components/ActionableComponent';
import { vSetTestId } from '@/common/components/ActionableComponent';
import excludeFalsy from '@/common/exclude-falsy';

export type ContentListItem =
  | {
      id?: string | number;
      label?: string;
      value?: unknown | undefined | (unknown | undefined)[];
      markdown?: string | null | false;
      message?: string | null | false;
      to?: ZvdZLocationRaw;
      href?: string | null | false;
    }
  | undefined;

enum ColType {
  label,
  value,
}

type Col = ContentListItem & {
  type: ColType;
  props: { cols: number; sm?: number; class: string };
};

const componentName = 'BaseContentList';

type Props = ActionableComponentProps & {
  items?: ContentListItem[];
  vertical?: boolean;
  portrait?: boolean;
  hideEmpty?: boolean;
  loading?: boolean;
};
const props = defineProps<Props>();

const { t } = useI18n();
const { d, n } = useI18n({ useScope: 'global' });

const { smAndDown } = useDisplay();
const isVertical = computed<boolean>(() =>
  props.vertical !== undefined ? props.vertical : smAndDown.value,
);
const items = computed(() =>
  excludeFalsy(
    props.hideEmpty
      ? props.items?.filter(item => !isEmpty(item?.value))
      : props.items,
  ),
);

const cols = computed(() => {
  const value: Col[] = [];
  (items.value || []).forEach(item => {
    if (item.label) {
      value.push({
        ...item,
        type: ColType.label,
        props: {
          cols: 12,
          ...(isVertical.value ? {} : { sm: props.portrait ? 6 : 4 }),
          class: 'font-weight-bold',
        },
      });
    }
    value.push({
      ...item,
      type: ColType.value,
      props: {
        cols: 12,
        ...(isVertical.value ? {} : { sm: props.portrait ? 6 : 8 }),
        class: `mb-3${!item.label ? ' font-weight-bold' : ''}`,
      },
    });
  });
  return value;
});
</script>
